import { zodResolver } from '@hookform/resolvers/zod'
import {
    Alert,
    Card,
    CardHeader,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AsyncSelect, Button, ControlledCheckBox, ControlledTextField } from 'src/components/forms'
import Select from 'src/components/forms/Select'
import { HttpCacheGrupo } from 'src/http/cache/httpCacheGrupo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields, EntityWithIdFields } from 'src/types'
import { HttpEtapasABM } from '../api/httpEtapasABM'
import { EtapaPermisoPostFields, EtapaPostFields, EtapaViewFields } from '../api/schemas'
import { Etapa } from '../types'

type GrupoEtapa = EntityWithIdAndDescription & {
    permisoTipo: number
}

const permisoTipoDefault = 1
const getPermisoTipo = (permisoTipo?: number | null) => permisoTipo ?? permisoTipoDefault

const schemaFormPostEtapa = z.object({
    codSistemaPrincipal: z.number().optional(),
    [EtapaPostFields.CodSistema]: z.coerce.number().positive('Debe seleccionar un sistema.'),
    [EtapaPostFields.NombreEtapa]: z.string().min(1, 'Debe ingresar un nombre.'),
    [EtapaPostFields.DiasDuracion]: z.coerce.number(),
    [EtapaPostFields.CostoBase]: z.coerce.number(),
    [EtapaPostFields.CostoDiario]: z.coerce.number(),
    [EtapaPostFields.FormAsociado]: z.string().nullable(),
    [EtapaPostFields.Activo]: z.boolean(),
    [EtapaPostFields.CodEtapaEstado]: z.coerce.number().positive('Debe seleccionar un estado.'),
    [EtapaPostFields.TipoEtapa]: z.string().min(1, 'Debe ingresar un tipo.').max(1, 'Debe ingresar un solo caracter.'),
    [EtapaPostFields.IdEtapaVirtual]: z.coerce.number(),
    [EtapaPostFields.Orden]: z.coerce.number(),
    [EtapaPostFields.IdEtapaAnular]: z.coerce.number().nullable(),
    [EtapaPostFields.PermiteAnularCreador]: z.boolean(),
    [EtapaPostFields.PermiteAnularActual]: z.boolean(),
    [EtapaPostFields.Permisos]: z
        .array(
            z.object({
                [EtapaPermisoPostFields.IdGrupo]: z.number(),
                [EtapaPermisoPostFields.PermisoTipo]: z.number()
            })
        )
        .nullable()
})

type SchemaFormPostEtapa = z.infer<typeof schemaFormPostEtapa>

type DialogEtapaABMProps = {
    closeDialog: () => void
    etapa?: Etapa
}

const defaultValues: SchemaFormPostEtapa = {
    codSistemaPrincipal: undefined,
    [EtapaPostFields.CodSistema]: 0,
    [EtapaPostFields.NombreEtapa]: '',
    [EtapaPostFields.DiasDuracion]: 3,
    [EtapaPostFields.CostoBase]: 0,
    [EtapaPostFields.CostoDiario]: 0,
    [EtapaPostFields.FormAsociado]: '',
    [EtapaPostFields.Activo]: true,
    [EtapaPostFields.CodEtapaEstado]: 0,
    [EtapaPostFields.TipoEtapa]: '',
    [EtapaPostFields.IdEtapaVirtual]: 0,
    [EtapaPostFields.Orden]: 0,
    [EtapaPostFields.IdEtapaAnular]: 0,
    [EtapaPostFields.PermiteAnularCreador]: false,
    [EtapaPostFields.PermiteAnularActual]: false,
    [EtapaPostFields.Permisos]: []
}

const mapEtapaToForm = (etapa?: Etapa): SchemaFormPostEtapa => ({
    ...defaultValues,
    ...(etapa
        ? {
              codSistemaPrincipal: etapa[EtapaViewFields.CodSistemaPrincipal],
              [EtapaPostFields.CodSistema]: etapa[EtapaViewFields.CodSistema],
              [EtapaPostFields.NombreEtapa]: etapa[EtapaViewFields.NombreEtapa],
              [EtapaPostFields.DiasDuracion]: etapa[EtapaViewFields.DiasDuracion],
              [EtapaPostFields.CostoBase]: etapa[EtapaViewFields.CostoBase],
              [EtapaPostFields.CostoDiario]: etapa[EtapaViewFields.CostoDiario],
              [EtapaPostFields.FormAsociado]: etapa[EtapaViewFields.FormAsociado] || '',
              [EtapaPostFields.Activo]: etapa[EtapaViewFields.Activo],
              [EtapaPostFields.CodEtapaEstado]: etapa[EtapaViewFields.CodEtapaEstado],
              [EtapaPostFields.TipoEtapa]: etapa[EtapaViewFields.TipoEtapa],
              [EtapaPostFields.IdEtapaVirtual]: etapa[EtapaViewFields.IdEtapaVirtual],
              [EtapaPostFields.Orden]: etapa[EtapaViewFields.Orden],
              [EtapaPostFields.IdEtapaAnular]: etapa[EtapaViewFields.IdEtapaAnular] || 0,
              [EtapaPostFields.PermiteAnularCreador]: !!etapa[EtapaViewFields.PermiteAnularCreador],
              [EtapaPostFields.PermiteAnularActual]: !!etapa[EtapaViewFields.PermiteAnularActual],
              [EtapaPostFields.Permisos]: etapa[EtapaViewFields.Permisos] || []
          }
        : {})
})

export const DialogEtapaABM = ({ closeDialog, etapa }: DialogEtapaABMProps) => {
    const [gruposPorSistema, setGruposPorSistema] = useState<GrupoEtapa[]>([])
    const [gruposAsignados, setGruposAsignados] = useState<GrupoEtapa[]>([])
    const [etapasSistema, setEtapasSistema] = useState<EntityWithIdAndDescription[]>([])
    const [idsDisponiblesSeleccionados, setIdsDisponiblesSeleccionados] = useState<number[]>([])
    const [idsAsignadosSeleccionados, setIdsAsignadosSeleccionados] = useState<number[]>([])

    const { control, handleSubmit, watch, setValue } = useForm<SchemaFormPostEtapa>({
        resolver: zodResolver(schemaFormPostEtapa),
        values: mapEtapaToForm(etapa)
    })

    const codSistemaSeleccionado = watch(EtapaPostFields.CodSistema)
    const permisosActuales = watch(EtapaPostFields.Permisos) || []
    const permiteAnularCreador = watch(EtapaPostFields.PermiteAnularCreador)
    const permiteAnularActual = watch(EtapaPostFields.PermiteAnularActual)
    const permiteConfigurarEtapaAnular = permiteAnularCreador || permiteAnularActual

    useEffect(() => {
        if (!permiteConfigurarEtapaAnular) {
            setValue(EtapaPostFields.IdEtapaAnular, null, { shouldDirty: true, shouldValidate: true })
        }
    }, [permiteConfigurarEtapaAnular, setValue])

    useEffect(() => {
        if (!codSistemaSeleccionado) {
            setGruposPorSistema([])
            setGruposAsignados([])
            setEtapasSistema([])
            setIdsDisponiblesSeleccionados([])
            setIdsAsignadosSeleccionados([])

            return
        }

        HttpCacheGrupo.getGruposPorSistema(codSistemaSeleccionado).then(grupos => {
            const permisosPorGrupo = new Map(
                permisosActuales.map(permiso => [
                    permiso[EtapaPermisoPostFields.IdGrupo],
                    getPermisoTipo(permiso[EtapaPermisoPostFields.PermisoTipo])
                ])
            )

            const gruposConPermiso = grupos.map(grupo => ({
                ...grupo,
                permisoTipo: getPermisoTipo(permisosPorGrupo.get(grupo[EntityWithIdFields.Id]))
            }))

            setGruposPorSistema(gruposConPermiso)
            setGruposAsignados(
                gruposConPermiso.filter(grupo => permisosPorGrupo.has(grupo[EntityWithIdFields.Id]))
            )
            setIdsDisponiblesSeleccionados([])
            setIdsAsignadosSeleccionados([])
        })

        HttpEtapasABM.getEtapasPorSistema(codSistemaSeleccionado).then(etapas => {
            setEtapasSistema([
                { id: 0, descripcion: 'Sin etapa de anulacion' },
                ...etapas
                    .filter(item => item.id !== etapa?.id && item[EtapaViewFields.Activo])
                    .map(item => ({
                        id: item.id,
                        descripcion: `${item[EtapaViewFields.IdEtapaVirtual]} - ${item[EtapaViewFields.NombreEtapa]}`
                    }))
            ])
        })
    }, [codSistemaSeleccionado])

    const gruposDisponibles = useMemo(() => {
        const asignados = new Set(gruposAsignados.map(grupo => grupo[EntityWithIdFields.Id]))

        return gruposPorSistema.filter(grupo => !asignados.has(grupo[EntityWithIdFields.Id]))
    }, [gruposPorSistema, gruposAsignados])

    const actualizarPermisosForm = (grupos: GrupoEtapa[]) => {
        const permisos = grupos.map(grupo => ({
            [EtapaPermisoPostFields.IdGrupo]: grupo[EntityWithIdFields.Id],
            [EtapaPermisoPostFields.PermisoTipo]: getPermisoTipo(grupo.permisoTipo)
        }))

        setValue(EtapaPostFields.Permisos, permisos, { shouldDirty: true, shouldValidate: true })
    }

    const toggleDisponibleSeleccionado = (idGrupo: number) => {
        setIdsDisponiblesSeleccionados(prev =>
            prev.includes(idGrupo) ? prev.filter(id => id !== idGrupo) : [...prev, idGrupo]
        )
    }

    const toggleAsignadoSeleccionado = (idGrupo: number) => {
        setIdsAsignadosSeleccionados(prev =>
            prev.includes(idGrupo) ? prev.filter(id => id !== idGrupo) : [...prev, idGrupo]
        )
    }

    const asignarGruposSeleccionados = () => {
        if (idsDisponiblesSeleccionados.length === 0) return

        const nuevosAsignados = [
            ...gruposAsignados,
            ...gruposDisponibles.filter(grupo => idsDisponiblesSeleccionados.includes(grupo[EntityWithIdFields.Id]))
        ]

        setGruposAsignados(nuevosAsignados)
        setIdsDisponiblesSeleccionados([])
        actualizarPermisosForm(nuevosAsignados)
    }

    const quitarGruposSeleccionados = () => {
        if (idsAsignadosSeleccionados.length === 0) return

        const nuevosAsignados = gruposAsignados.filter(grupo => !idsAsignadosSeleccionados.includes(grupo[EntityWithIdFields.Id]))

        setGruposAsignados(nuevosAsignados)
        setIdsAsignadosSeleccionados([])
        actualizarPermisosForm(nuevosAsignados)
    }

    const onPermisoTipoChange = (idGrupo: number, permisoTipo: number) => {
        const actualizarGrupo = (grupo: GrupoEtapa) =>
            grupo[EntityWithIdFields.Id] === idGrupo ? { ...grupo, permisoTipo } : grupo

        const nuevosAsignados = gruposAsignados.map(actualizarGrupo)
        const nuevosGrupos = gruposPorSistema.map(actualizarGrupo)

        setGruposAsignados(nuevosAsignados)
        setGruposPorSistema(nuevosGrupos)
        actualizarPermisosForm(nuevosAsignados)
    }

    const submitEtapa = (data: SchemaFormPostEtapa) => {
        const payload = {
            ...data,
            [EtapaPostFields.FormAsociado]: data[EtapaPostFields.FormAsociado] || null,
            [EtapaPostFields.IdEtapaAnular]: permiteConfigurarEtapaAnular ? data[EtapaPostFields.IdEtapaAnular] || null : null
        }

        return etapa ? HttpEtapasABM.putEtapa(etapa.id, payload) : HttpEtapasABM.postEtapa(payload)
    }

    const onSubmitWrapper = () => {
        return new Promise<void>((resolve, reject) => {
            handleSubmit(
                formData => {
                    submitEtapa(formData)
                        .then(() => {
                            setTimeout(closeDialog, 500)
                            resolve()
                        })
                        .catch(reject)
                },
                validationErrors => {
                    reject(validationErrors)
                }
            )()
        })
    }

    return (
        <>
            <DialogTitle>{etapa ? 'Editar etapa' : 'Crear nueva etapa'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Stack spacing={2}>
                            <Typography>General</Typography>
                            <Divider />
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <ControlledTextField control={control} name={EtapaPostFields.NombreEtapa} label='Nombre' fullWidth />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <Select control={control} name='codSistemaPrincipal' label='Sistema principal' loadOptions={HttpCacheWorkflow.getSistemasPrincipales} />
                                </Grid>
                                <Grid item md={9} xs={12}>
                                    <AsyncSelect
                                        control={control}
                                        name={EtapaPostFields.CodSistema}
                                        label='Sistema'
                                        loadOptions={() => HttpCacheWorkflow.getSistemasPorSistemaPrincipal(watch('codSistemaPrincipal') || 0)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledTextField control={control} name={EtapaPostFields.DiasDuracion} label='Dias duracion' type='number' fullWidth />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <Select control={control} name={EtapaPostFields.CodEtapaEstado} label='Estado' loadOptions={HttpCacheWorkflow.getEtapaEstados} />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledTextField control={control} name={EtapaPostFields.CostoBase} label='Costo base' type='number' fullWidth />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledTextField control={control} name={EtapaPostFields.CostoDiario} label='Costo diario' type='number' fullWidth />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledTextField control={control} name={EtapaPostFields.IdEtapaVirtual} label='Id etapa virtual' type='number' fullWidth />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledTextField control={control} name={EtapaPostFields.Orden} label='Orden' type='number' fullWidth />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledTextField
                                        control={control}
                                        name={EtapaPostFields.TipoEtapa}
                                        label='Tipo etapa'
                                        inputProps={{ maxLength: 1 }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledCheckBox control={control} name={EtapaPostFields.Activo} label='Activa' />
                                </Grid>
                                <Grid item xs={12}>
                                    <ControlledTextField control={control} name={EtapaPostFields.FormAsociado} label='Form asociado' fullWidth />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Select
                                        control={control}
                                        name={EtapaPostFields.IdEtapaAnular}
                                        label='Etapa de anulacion'
                                        loadOptions={etapasSistema}
                                        disabled={!permiteConfigurarEtapaAnular}
                                    />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledCheckBox control={control} name={EtapaPostFields.PermiteAnularCreador} label='Anula creador' />
                                </Grid>
                                <Grid item md={3} xs={6}>
                                    <ControlledCheckBox control={control} name={EtapaPostFields.PermiteAnularActual} label='Anula actual' />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={2}>
                            <Typography>Grupos</Typography>
                            <Divider />
                            {!codSistemaSeleccionado ? (
                                <Alert severity='info'>Seleccione un sistema para ver los grupos.</Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={5.5}>
                                        <Card variant='outlined'>
                                            <CardHeader title='Disponibles' titleTypographyProps={{ variant: 'subtitle2' }} />
                                            <List dense sx={{ height: 300, overflow: 'auto' }}>
                                                {gruposDisponibles.map(grupo => {
                                                    const idGrupo = grupo[EntityWithIdFields.Id]

                                                    return (
                                                        <ListItemButton key={idGrupo} onClick={() => toggleDisponibleSeleccionado(idGrupo)}>
                                                            <ListItemIcon>
                                                                <Checkbox edge='start' checked={idsDisponiblesSeleccionados.includes(idGrupo)} tabIndex={-1} disableRipple />
                                                            </ListItemIcon>
                                                            <ListItemText primary={grupo[EntityWithIdAndDescriptionFields.Description]} />
                                                        </ListItemButton>
                                                    )
                                                })}
                                            </List>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={1}>
                                        <Stack spacing={1} alignItems='center' justifyContent='center' sx={{ height: 1, minHeight: 72 }}>
                                            <Button variant='outlined' color='primary' onClick={asignarGruposSeleccionados} disabled={idsDisponiblesSeleccionados.length === 0}>
                                                &gt;
                                            </Button>
                                            <Button variant='outlined' color='primary' onClick={quitarGruposSeleccionados} disabled={idsAsignadosSeleccionados.length === 0}>
                                                &lt;
                                            </Button>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={5.5}>
                                        <Card variant='outlined'>
                                            <CardHeader title='Asignados' titleTypographyProps={{ variant: 'subtitle2' }} />
                                            <List dense sx={{ height: 300, overflow: 'auto' }}>
                                                {gruposAsignados.map(grupo => {
                                                    const idGrupo = grupo[EntityWithIdFields.Id]

                                                    return (
                                                        <ListItem
                                                            key={idGrupo}
                                                            disablePadding
                                                            secondaryAction={
                                                                <ToggleButtonGroup
                                                                    exclusive
                                                                    size='small'
                                                                    value={String(getPermisoTipo(grupo.permisoTipo))}
                                                                    onClick={event => event.stopPropagation()}
                                                                    onChange={(_, value) => value !== null && onPermisoTipoChange(idGrupo, Number(value))}
                                                                    sx={{ flexShrink: 0 }}
                                                                >
                                                                    <ToggleButton value='0' sx={{ fontSize: '0.68rem', px: 0.75 }}>
                                                                        0
                                                                    </ToggleButton>
                                                                    <ToggleButton value='1' sx={{ fontSize: '0.68rem', px: 0.75 }}>
                                                                        1
                                                                    </ToggleButton>
                                                                    <ToggleButton value='2' sx={{ fontSize: '0.68rem', px: 0.75 }}>
                                                                        2
                                                                    </ToggleButton>
                                                                    <ToggleButton value='3' sx={{ fontSize: '0.68rem', px: 0.75 }}>
                                                                        3
                                                                    </ToggleButton>
                                                                </ToggleButtonGroup>
                                                            }
                                                        >
                                                            <ListItemButton onClick={() => toggleAsignadoSeleccionado(idGrupo)} sx={{ pr: 17 }}>
                                                                <ListItemIcon>
                                                                    <Checkbox edge='start' checked={idsAsignadosSeleccionados.includes(idGrupo)} tabIndex={-1} disableRipple />
                                                                </ListItemIcon>
                                                                <ListItemText primary={grupo[EntityWithIdAndDescriptionFields.Description]} />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    )
                                                })}
                                            </List>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='text' color='primary' onClick={closeDialog}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}
